import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Download, FileSpreadsheet, Plus } from "lucide-react";

import { clx } from "../../../utils/Helpers";
import "./UIDocumentRow.scss";

const UIDocumentRow = ({
  title,
  subtitle,
  statusText,
  statusTone,
  href,
  downloadLabel,
  uploadLabel,
  onFileSelect,
  accept,
  uploadDisabled,
  className,
  icon,
}) => {
  const fileInputRef = useRef(null);
  const isMissing = !href;
  const metaText = subtitle || (!isMissing ? statusText : "") || "";
  const showUpload = isMissing && typeof onFileSelect === "function";

  const handleUploadClick = () => {
    if (uploadDisabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
    event.target.value = "";
  };

  return (
    <div
      className={clx(
        "ui-document-row",
        isMissing ? "ui-document-row--missing" : "ui-document-row--available",
        className,
      )}
    >
      <div className="ui-document-row__content">
        <div className="ui-document-row__icon-box" aria-hidden>
          {icon || <FileSpreadsheet size={18} strokeWidth={1.75} />}
        </div>
        <div className="ui-document-row__main">
          <span className="ui-document-row__title">{title}</span>
          {metaText ? (
            <span className="ui-document-row__subtitle">{metaText}</span>
          ) : null}
          {isMissing && statusText ? (
            <span
              className={clx(
                "ui-document-row__status",
                statusTone && `ui-document-row__status--${statusTone}`,
              )}
            >
              {statusText}
            </span>
          ) : null}
        </div>
      </div>
      {!isMissing && href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="ui-document-row__download"
          aria-label={downloadLabel || title}
        >
          <Download size={16} strokeWidth={1.75} />
        </a>
      ) : null}
      {showUpload ? (
        <>
          <button
            type="button"
            className="ui-document-row__upload"
            aria-label={uploadLabel || title}
            disabled={uploadDisabled}
            onClick={handleUploadClick}
          >
            <Plus size={20} strokeWidth={1.75} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            className="ui-document-row__file-input"
            disabled={uploadDisabled}
            onChange={handleFileChange}
          />
        </>
      ) : null}
    </div>
  );
};

UIDocumentRow.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  statusText: PropTypes.string,
  statusTone: PropTypes.oneOf(["muted", "warning", "error"]),
  href: PropTypes.string,
  downloadLabel: PropTypes.string,
  uploadLabel: PropTypes.string,
  onFileSelect: PropTypes.func,
  accept: PropTypes.string,
  uploadDisabled: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.element,
};

UIDocumentRow.defaultProps = {
  subtitle: "",
  statusText: "",
  statusTone: "muted",
  href: "",
  downloadLabel: "",
  uploadLabel: "",
  onFileSelect: undefined,
  accept: ".pdf",
  uploadDisabled: false,
  className: "",
  icon: null,
};

export default UIDocumentRow;
